import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useTheme } from '../lib/theme';
import { useAmbientBackground } from '../hooks/useAmbientBackground';
import { useOrbPosition } from '../hooks/useOrbPosition';
import { TemperatureUnitProvider } from '../contexts/TemperatureUnitContext';
import WeatherHeader from '../components/WeatherHeader';
import ActivitySelector from '../components/ActivitySelector';
import Weather from '../components/Weather';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';
import { useGlobalCounter } from '../hooks/useGlobalCounter';
import FlipCounter from '../components/FlipCounter';
import '../styles/hourly-gradients.css';
import '../styles/atmospheric-noise.css';

const Index = () => {
  const navigate = useNavigate();
  const { isOnboarded, loading, user, preferences } = useUserPreferences();
  const [selectedActivity, setSelectedActivity] = useState('walk');
  const { theme, toggleTheme } = useTheme();
  const { gradientClass } = useAmbientBackground();
  const orbPosition = useOrbPosition();
  const { count, increment, isLoading } = useGlobalCounter();

  // Handle authenticated users
  useEffect(() => {
    if (!loading && user && isOnboarded) {
      navigate('/personalized');
    }
  }, [loading, user, isOnboarded, navigate]);

  // Show loading state
  if (loading) {
    return (
      <div className={`min-h-screen bg-background ${gradientClass} flex items-center justify-center font-inter`}>
        <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
      </div>
    );
  }

  // Show onboarding for authenticated but not onboarded users
  if (user && !isOnboarded) {
    return <OnboardingFlow />;
  }

  // Basic page for everyone else (guests and first-time visitors)
  return (
    <TemperatureUnitProvider>
      <div className={`min-h-screen bg-background ${gradientClass} flex flex-col items-center justify-center p-4 relative font-inter`}>
        <div className="noise-overlay"></div>
        <div 
          className="orb-effect" 
          style={{
            top: `${orbPosition.top}%`,
            left: `${orbPosition.left}%`
          }}
        ></div>

        <div className="flex justify-between items-center w-full max-w-md mb-4">
          <WeatherHeader theme={theme} onThemeToggle={toggleTheme} showBanner={false} />
        </div>

        <FlipCounter count={count} isLoading={isLoading} />

        {/* Activity Selector - Prominent at top */}
        <div className="w-full max-w-md mb-6">
          <ActivitySelector 
            selectedActivity={selectedActivity}
            onActivityChange={setSelectedActivity}
          />
        </div>

        <h1 className="text-h2 font-light text-foreground mb-6 text-center px-2">
          How many layers should I wear today?
        </h1>

        <div className="w-full max-w-md">
          <Weather 
            theme={theme} 
            onRecommendationShown={increment}
            activityMode={selectedActivity}
            isPersonalized={false}
          />
        </div>

        {/* Sign up prompt for guests */}
        {!user && (
          <div className="mt-6 text-center">
            <p className="text-body-sm text-muted-foreground mb-2">
              Want personalized recommendations?
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="px-4 py-2 bg-primary text-primary-foreground text-button rounded-lg transition-colors duration-200 hover:bg-primary/90"
            >
              Sign Up for Free
            </button>
          </div>
        )}
      </div>
    </TemperatureUnitProvider>
  );
};

export default Index;